<template>
    <div :class="props.customClass">
        <ValidationProvider :name="props.name" :rules="computedRules" :value="localValue" v-slot="{ errors }" mode="eager" ref="provider">
            <div class="tw:relative tw:w-full" :data-validation-error="errors[0] ? '' : undefined">
                <div class="input-select-multiselect" :class="{ 'input-select-error': errors[0] }" :style="(props.disabled || props.loading) ? 'cursor: not-allowed' : ''">
                    <multiselect
                        :data-cy="props.dataCy || props.name"
                        :value="selectedOptions"
                        :options="props.options"
                        :label="props.labelKey"
                        :track-by="props.valueKey"
                        :placeholder="errors[0] ? ' ' : (props.placeholder || ' ')"                        
                        :disabled="props.disabled || props.loading"
                        :multiple="props.multiple"
                        :searchable="true"
                        :allow-empty="true"
                        :close-on-select="!props.multiple"
                        :show-labels="false"
                        :loading="props.loading"
                        v-bind="$attrs"
                        @input="handleSelect"
                        @open="isOpen = true"
                        @close="handleClose"
                    >
                        <template
                            v-if="$scopedSlots.option"
                            slot="option"
                            slot-scope="{ option, search, id }"
                        >
                            <slot
                                name="option"
                                :option="option"
                                :search="search"
                                :id="id"
                            ></slot>
                        </template>
                        <template
                            v-if="$scopedSlots.singleLabel"
                            slot="singleLabel"
                            slot-scope="{ option }"
                        >
                            <slot
                                name="singleLabel"
                                :option="option"
                            ></slot>
                        </template>
                    </multiselect>
                </div>
                <span
                    v-if="props.label"
                    class="tw:absolute tw:uppercase tw:duration-200 tw:tracking-wide tw:origin-left tw:px-1 tw:start-1.5 tw:z-[1] tw:pointer-events-none"
                    :class="[
                        (isOpen || !isEmpty)
                            ? 'tw:-top-[10px] tw:scale-75 tw:bg-white tw:font-bold'
                            : 'tw:top-1/2 tw:-translate-y-1/2 tw:scale-100 tw:bg-transparent',
                        errors[0]
                            ? 'tw:text-red-400'
                            : (isOpen ? 'tw:text-blue-800 tw:font-bold' : 'tw:text-slate-600')
                    ]"
                >
                    {{ props.label }}
                </span>
            </div>
            <p class="tw:text-xs tw:m-0 tw:text-slate-400 tw:font-medium">{{ props.description }}</p>
            <span v-if="errors[0]" class="tw:block tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium text-left">
                {{ formatErrorMessage(errors[0]) }}
            </span>

            <span
                v-if="props.apiValidationError && !errors[0]"
                class="tw:block tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium"
            >
                {{ Array.isArray(props.apiValidationError) ? props.apiValidationError.join(', ') : props.apiValidationError }}
            </span>
        </ValidationProvider>
    </div>
</template>

<script setup>
    import { ref, computed, watch } from 'vue';

    const props = defineProps({
        label: String,
        name: String,
        rules: [String, Object],
        value: [String, Number, Array],
        options: { type: Array, default: () => [] },
        valueKey: { type: String, default: 'id' },
        labelKey: { type: String, default: 'name' },
        placeholder: String,
        disabled: Boolean,
        loading: Boolean,
        multiple: { type: Boolean, default: false },
        apiValidationError: [String, Array],
        description: String,
        customClass: String,
        dataCy: String
    });

    const emit = defineEmits(['input', 'validation-error']);
    const provider = ref(null);
    const isOpen = ref(false);

    const computedRules = computed(() => props.rules || '');

    const localValue = ref(
        props.multiple
            ? (Array.isArray(props.value) ? [...props.value] : [])
            : (props.value != null ? props.value : '')
    );

    // Null-safe: vue-multiselect emits `null` when the user DESELECTS the
    // current option (single mode). `typeof null === 'object'`, so the old
    // implementation tried `null[props.valueKey]` and crashed the render
    // ("Cannot read properties of null (reading 'id')"), leaving the select
    // visually stuck. With the guard, null flows through the comparison
    // harmlessly and `selectedOptions` correctly resolves to null.
    const getOptionValue = opt => (opt !== null && typeof opt === 'object' ? opt[props.valueKey] : opt);

    // Comparación tolerante a string/number: los valores hidratados desde la URL
    // llegan como string ('1268') mientras los options traen ids numéricos (1268).
    const sameValue = (a, b) => String(a) === String(b);

    const selectedOptions = computed(() => {
        if (props.multiple) {
            // Antes: `ids.includes(getOptionValue(o))` solo funcionaba si
            // localValue.value era un array de IDs (number). Si era un array
            // de objetos (que es lo que esperan los BaseMultiselects que
            // guardan objetos para matchear lo que el backend recibe de
            // master vía v-model + track-by), la comparación `objeto.includes(número)`
            // siempre es false y no se mostraban los items seleccionados.
            //
            // Se combinan las dos mejoras: `some` con `getOptionValue` en ambos
            // lados acepta arrays de IDs Y de objetos, y `sameValue` tolera que
            // el id llegue como string ('1268') mientras el option lo trae
            // numérico (1268).
            const selected = localValue.value;
            return props.options.filter(o =>
                selected.some(s => sameValue(getOptionValue(s), getOptionValue(o)))
            );
        }
        const val = localValue.value;
        if (val === '' || val === null || val === undefined) return null;
        return props.options.find(o => sameValue(getOptionValue(o), getOptionValue(val))) || null;
    });

    const isEmpty = computed(() =>
        props.multiple ? localValue.value.length === 0 : (!localValue.value && localValue.value !== 0)
    );
    const handleClose = async() => {
        isOpen.value = false;
        const result = await provider.value?.validate(selectedOptions.value);
        if (result && !result.valid) {
            emit('validation-error');
        }
    };

    const formatErrorMessage = err => {
        const msg = typeof err === 'string' ? err : (err && err.message) || String(err || '');
        return msg ? msg.charAt(0).toUpperCase() + msg.slice(1).toLowerCase() : '';
    };

    const handleSelect = val => {
        if (props.multiple) {
            const arr = Array.isArray(val) ? val : [];
            const newVal = arr.map(v => getOptionValue(v));
            localValue.value = newVal;
            emit('input', newVal);
        } else {
            const newVal = val ? getOptionValue(val) : null;
            localValue.value = newVal;
            emit('input', newVal);
        }
    };

    watch(() => props.value, newVal => {
        if (props.multiple) {
            localValue.value = Array.isArray(newVal) ? [...newVal] : [];
        } else {
            localValue.value = newVal != null ? newVal : '';
        }
    });
    
</script>

<script>
    export default {
        inheritAttrs: false,
    };
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
.input-select-multiselect :deep(.multiselect__tags) {
    display: flex;
    flex-direction:column-reverse;
    min-height: 38px;
    padding: 8px 32px 8px 11px;
    border-radius: 4px;
    border: 1px solid #cad5e2;
    background: white;
    cursor: pointer;
}
.input-select-multiselect :deep(.multiselect__content-wrapper) {
    border-radius: 4px;
    border: 1px solid #8cbdfa;
    margin: 1px 0 7px 0 ;
    z-index: 100;
    box-shadow: 0 5px 20px rgba(15, 23, 42, 0.12);
}
.input-select-multiselect :deep(.multiselect__option) {
    padding: 5px 8px;
    font-size: 14px;
    margin-bottom: 12px;
    border: 1px solid transparent;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    min-height: 38px;
}
.input-select-multiselect :deep(.multiselect__placeholder) {
    padding:  0;
    margin: 0;
    line-height: normal;
}
.input-select-multiselect.input-select-error :deep(.multiselect--active .multiselect__tags) {
    border-color: #60a5fa;
    background-color: white;
}
.input-select-multiselect :deep(.multiselect--active .multiselect__tags) {
    border-color: #8cbdfa;
    background-color: white;
    color: #60a5fa
}

.input-select-multiselect.input-select-error :deep(.multiselect__tags) {
    border-color: #ef4444;
    background-color: #fef2f280;
}
.input-select-multiselect :deep(.multiselect__option--selected) {
    color: #ffffff;
    background-color: #60a5fa;
    border-radius: 4px;
}
.input-select-multiselect :deep(.multiselect__option--highlight) { 
    color: #60a5fa;
    background-color: #ffffff;
    border-radius: 4px;
    border: 1px solid #8cbdfa;
}
.input-select-multiselect :deep(.multiselect__option--selected.multiselect__option--highlight) {
    color: #ffffff;
    background-color: #60a5fa;
    border: 1px solid #60a5fa;
    border-radius: 4px;
}
.input-select-multiselect :deep(.multiselect__content) {
    padding: 12px 12px 0 12px;
    border-radius: 4px;
}
.input-select-multiselect :deep(.multiselect) {
    min-height: 38px;
}
.input-select-multiselect :deep(.multiselect--active) {
    z-index: auto;
}
.input-select-multiselect :deep(.multiselect__single) {
    color: inherit;
    margin: 0;
    padding: 0;
    font-size: 14px;
}
.input-select-multiselect :deep(.multiselect--active .multiselect__single) {
    color: #60a5fa;
}
.input-select-multiselect :deep(.multiselect--active .multiselect__input) {
    color: #60a5fa; 
    caret-color: #60a5fa;
}
.input-select-multiselect :deep(.multiselect__tag) {
    display: flex;
    align-items: center;
    width: fit-content;
    background-color: #60a5fa;
    border-radius: 4px;
    color: #fff;
    margin: 3px 0 2px 0;
    padding: 3px 6px;
}
.input-select-multiselect :deep(.multiselect__tag-icon) {
    position: relative;
    height: 100%;
    width: 100%;
    margin: 0 0 0 4px;
    padding: 0;
    border-radius: 0;
    line-height: 9px;
}
.input-select-multiselect :deep(.multiselect__tag-icon:hover) {
    transform: scale(1.1);
    transition: transform 0.1s ease-in-out;
}
.input-select-multiselect :deep(.multiselect__tag-icon:hover::after) {
    color: #ff01019b;
}
.input-select-multiselect :deep(.multiselect__tags-wrap) {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
}
.input-select-multiselect :deep(.multiselect__input) {
    border: 1px solid transparent;
    border-radius: 4px;
    margin: 0;
    line-height: 17px;
}
.input-select-multiselect :deep(.multiselect--disabled .multiselect__select) {
    background: transparent;
}
</style>
